import { JwtPayload, jwtDecode } from "jwt-decode";
import { AuthenticationResponse, tmpStorage } from "@/api/login";

enum keys {
  ACCESS_TOKEN = "access_token",
  JTI = "jti",
  REFRESH_TOKEN = "refresh_token",
  SCOPE = "scope",
  TOKEN_EXPIRED = "exp",
  TOKEN_TYPE = "token_type",
  USER_ID = "user_id",
  USER_NAME = "user_name",
  CSF_USER_ID = "csf_user_id",
}

export const saveResponseInLocalStorage = (response: Record<string, string | number>) => {
  for (const [key, value] of Object.entries(response)) {
    // JSON.stringify(value) needs to be changed to vlaue.toString() in the future when monolith local storage is fixed.
    localStorage.setItem(key, JSON.stringify(value));
  }
};

const checkIfParseNeeded = (val: string) => {
  // this try catch checks if the value needs to be parse because it was stored stringified (because it is an object or by mistake)
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
};

export const getLocalStorageValue = (key: string) => {
  // this try catch checks if the local storage is accessible
  try {
    const val = localStorage.getItem(key);
    if (val) {
      return checkIfParseNeeded(val);
    }
  } catch (e) {
    console.error("Cannot access local storage.", e);
    return tmpStorage ? tmpStorage[key as keyof AuthenticationResponse] : undefined;
  }
};

export const getAccessToken = () => {
  return getLocalStorageValue("access_token");
};

export const getUserId = () => {
  return getLocalStorageValue("user_id");
};

export const getRefreshToken = () => {
  return getLocalStorageValue("refresh_token");
};

export const storageAvailable = () => {
  try {
    const test = "__local_storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    console.error("localStorage is not available");
    return false;
  }
};

export const isAuthenticated = () => {
  const token = storageAvailable() ? getRefreshToken() : tmpStorage?.refresh_token;
  return !isExpired(token);
};

interface jwtI extends JwtPayload {
  client_id: string;
  exp: number;
}

export const decodeToken = (token?: string | null): undefined | jwtI => {
  if (typeof token === "string") {
    const decoded: jwtI = jwtDecode(token);
    return decoded === null ? undefined : decoded;
  }
};

export const getExpiryDate = (accessToken?: string | null) => {
  const decoded = decodeToken(accessToken);
  if (decoded) {
    return new Date(decoded[keys.TOKEN_EXPIRED] * 1000);
  }
};

export const isExpired = (accessToken?: string | null) => {
  const expiryDate = getExpiryDate(accessToken);
  return !expiryDate || Date.now() >= expiryDate.getTime();
};

export const setAfterLoginRedirectURL = (url: string) => {
  localStorage.setItem("afterLoginRedirectURL", url);
};

export const getAfterLoginRedirectURL = () => {
  const filter = ["/newsletter/subscribe"];
  const redirectUrl = localStorage.getItem("afterLoginRedirectURL");
  return redirectUrl && filter.includes(redirectUrl) ? "" : redirectUrl;
};

export const delAfterLoginRedirectURL = () => {
  localStorage.removeItem("afterLoginRedirectURL");
};

export const setCSFUserId = (userId: string) => {
  localStorage.setItem(keys.CSF_USER_ID, userId);
};

export const getCSFUserId = () => {
  return localStorage.getItem(keys.CSF_USER_ID);
};
